import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type { Page } from "@csa/web-foundation";
import type {
  CreateStaffInput,
  ResetStaffPasswordResult,
  StaffCreatedSummary,
  StaffManagedSummary,
  StaffSummary,
  UpdateStaffInput,
} from "../../api/iam-contracts";

// IAM staff provisioning + management against /v1/admin/staff. No business logic in
// the browser: the API is the boundary and re-enforces staff:read / staff:write
// (+ staff.admin:write for elevated targets, + a self-disable guard) on every call.
// Any returned one-time temporary password is NEVER cached or invalidated — it lives
// only in the surface's reveal panel until dismissed.

const STAFF = ["admin", "staff"];

/** POST /v1/admin/staff — provision a new staff login. staff:write@all (elevated roles
 *  + cross-org additionally need staff.admin:write). Returns the created summary plus a
 *  ONE-TIME temporary password. On success the list is invalidated so the new staffer
 *  appears. */
export function useCreateStaff() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateStaffInput) =>
      api.post<StaffCreatedSummary>("/v1/admin/staff", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF });
    },
  });
}

/** GET /v1/admin/staff — the org-scoped staff list (Page envelope, keyset cursor).
 *  staff:read@all. `enabled` skips the doomed read when the caller lacks the grant.
 *  Keyset "daha fazla" pagination via nextCursor. */
export function useStaffList(enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...STAFF, "list"],
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      api.get<Page<StaffSummary>>(
        pageParam ? `/v1/admin/staff?cursor=${encodeURIComponent(pageParam)}` : "/v1/admin/staff",
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
}

/** PATCH /v1/admin/staff/:id — update displayName and/or roleCode. staff:write@all
 *  (elevated role changes need staff.admin:write). On success, refetch the list. */
export function useUpdateStaff(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateStaffInput) =>
      api.patch<StaffManagedSummary>(`/v1/admin/staff/${id}`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF });
    },
  });
}

/** POST /v1/admin/staff/:id/status — enable/disable a staff login. staff:write@all
 *  (elevated target needs staff.admin:write; the API forbids disabling self). On
 *  success, refetch the list. */
export function useSetStaffStatus(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<StaffManagedSummary>(`/v1/admin/staff/${id}/status`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: STAFF });
    },
  });
}

/** POST /v1/admin/staff/:id/reset-password — set a one-time temporary password on the
 *  staffer's login. staff:write@all (elevated target needs staff.admin:write). The temp
 *  value is NEVER cached/invalidated — it lives only in the reveal panel. */
export function useResetStaffPassword(id: string) {
  const api = useApi();
  return useMutation({
    mutationFn: () => api.post<ResetStaffPasswordResult>(`/v1/admin/staff/${id}/reset-password`),
  });
}
